// components/MermaidChart.js
import { use, useCallback, useEffect, useMemo, useRef, useState } from "react";
import mermaid from "mermaid";
import { Terminal } from "lucide-react";
import domtoimage from "dom-to-image";
import {
  CopyOutlined,
  DownloadOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import copy from "copy-to-clipboard";
import styles from "./mermaidChart.module.scss";
import { useRouter } from "next/router";
import {
  changeHistory,
  changeWikiHistory,
  umlReChat,
  wikiUmlReChat,
} from "../api/mermaidChart";
import { Buffer } from "buffer";
import { Button, ImageViewer, Result, Segmented, Space } from "antd-mobile";
import { ShowMessage } from "../ShowMessage/ShowMessage";
import { useTheme } from "../../context/ThemeContext";

const MermaidChart = (props: {
  chart: string;
  uuid: string;
  historyItem: string;
  thread_id: string;
  title?: string;
  type?: "wiki" | "eagle";
  gotoBottom: () => void;
  umlReloadSuccess?: (str: string, uuid?: string) => void;
}) => {
  const router = useRouter();
  const { chart, umlReloadSuccess } = props;
  const chartRef = useRef<any>(null);
  const chartCodeRef = useRef<any>(null);
  const { theme } = useTheme();
  const [show, setShow] = useState<any>("图表");
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);
  const [downloadLoading, setIsDownloadLoading] = useState(false);
  const [copyLoading, setCopyLoading] = useState(false);
  const [isChartRight, setIsChartRight] = useState(true);
  const [chartContent, setChartContent] = useState(chart);
  const [requeryLoading, setRequeryLoading] = useState(false);
  const responseData = useRef("");
  const requeryCount = useRef(0);
  const mermaidRef = useRef<any>(null);
  const [isChartVisible, setIsChartVisible] = useState(false);

  useEffect(() => {
    setChartContent(chart);
  }, [chart]);

  useEffect(() => {
    const ref = chartRef.current;
    if (!ref) return;
    const observer = new window.IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsChartVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      {
        root: null,
        rootMargin: "100px",
        threshold: 0,
      }
    );
    observer.observe(ref);
    return () => observer.disconnect();
  }, [chartRef]);

  const loadMermaid = useCallback(
    (chart: string, reload?: boolean, limit = 2) => {
      mermaid
        .parse(chart)
        .then((parsedChart) => {
          setIsChartRight(true);
          setShow("图表");
          setRequeryLoading(false);
          setTimeout(async () => {
            const id = `mermaid-svg-${new Date().getTime()}`;
            const { svg } = await mermaid.render(id, chart);
            if (chartRef.current) chartRef.current.innerHTML = svg;
            if (props.type === "eagle") {
              props.gotoBottom();
            }
          }, 100);
          if (reload) {
            let newStr = props.historyItem.replace(
              props.chart,
              responseData.current
                .replace(/```mermaid\n/, "")
                .replace(/```/, "")
            );
            if (props.type === "wiki") {
              changeWikiHistory({
                wiki_id: router.query.wiki_id?.toString() || "",
                title: props.title || "",
                content: newStr,
              }).then(() => {
                umlReloadSuccess?.(newStr);
              });
            } else {
              changeHistory({
                history_id: props.uuid,
                text: newStr,
              }).then(() => {
                umlReloadSuccess?.(newStr, props.uuid);
              });
            }
          }
        })
        .catch((error) => {
          setIsChartRight(false);
          if (requeryCount.current < limit) {
            if (
              !JSON.parse(
                sessionStorage.getItem("allowUmlLoadIds") || "[]"
              ).includes(props.uuid)
            ) {
              setRequeryLoading(true);
              if (props.type === "wiki") {
                const params = {
                  title: props.title || "",
                  chart: chart,
                  errorMsg: error?.toString(),
                };
                wikiUmlReChat(params).then((res) => {
                  if (res.data) {
                    responseData.current = res.data;
                    const str = res.data
                      .replace(/```mermaid\n/, "")
                      .replace(/\n```/, "");
                    setChartContent(str);
                    requeryCount.current = requeryCount.current + 1;
                    loadMermaid(str, true, limit);
                  }
                });
              } else {
                const params = {
                  thread_id: props.thread_id || "",
                  chart: chart,
                  errorMsg: error?.toString(),
                };
                umlReChat(params).then((res) => {
                  if (res.data) {
                    responseData.current = res.data;
                    const str = res.data
                      .replace(/```mermaid\n/, "")
                      .replace(/\n```/, "");
                    setChartContent(str);
                    requeryCount.current = requeryCount.current + 1;
                    loadMermaid(str, true, limit);
                  }
                });
              }
            }
          } else {
            setRequeryLoading(false);
            sessionStorage.setItem(
              "allowUmlLoadIds",
              JSON.stringify([
                ...JSON.parse(
                  sessionStorage.getItem("allowUmlLoadIds") || "[]"
                ),
                props.uuid,
              ])
            );
          }
        });
    },
    [props, router.query.wiki_id, umlReloadSuccess]
  );
  const initMermaid = useCallback(
    (chartContent: string, theme: string) => {
      mermaid.initialize({
        startOnLoad: false,
        theme: theme === "dark" ? "dark" : "default",
        gantt: {
          useWidth: chartRef.current?.offsetWidth, // Set the width of the chart to the width of the container.
          useMaxWidth: true, // When this flag is set, the width of the chart will be set to the maximum width of the container.
        },
      });
      if (requeryCount.current === 0) {
        mermaid
          ?.parse(chartContent)
          .then(async () => {
            const id = `mermaid-svg-${new Date().getTime()}`;
            const { svg } = await mermaid.render(id, chartContent);
            if (chartRef.current) chartRef.current.innerHTML = svg;
          })
          .catch((err) => {
            setIsChartRight(false);
            if (props.type === "eagle") {
              if (props.thread_id === router.query.thread_id?.toString()) {
                loadMermaid(chartContent);
              }
            } else {
              loadMermaid(chartContent);
            }
          });
      }
    },
    [loadMermaid, props.thread_id, props.type, router.query.thread_id]
  );

  useEffect(() => {
    if (isChartVisible && show === "图表" && chartContent) {
      requestAnimationFrame(() => {
        initMermaid(chartContent, theme);
      });
    }
  }, [isChartVisible, show, chartContent, theme, initMermaid]);

  const downLoadSvgToPng = () => {
    if (downloadLoading) {
      return;
    }
    setIsDownloadLoading(true);
    const svg = chartRef.current;
    if (!svg) {
      return;
    }
    // 获取原始尺寸
    const originalWidth = svg.offsetWidth;
    const originalHeight = svg.offsetHeight;
    // 设置放大倍数
    const scaleFactor = 10;
    domtoimage
      .toPng(svg, {
        width: originalWidth * scaleFactor,
        height: originalHeight * scaleFactor,
        style: {
          transform: `scale(${scaleFactor})`,
          transformOrigin: "top left",
          width: `${originalWidth}px`,
          height: `${originalHeight}px`,
          backgroundColor: theme == "dark" ? "#282828" : "#F9F9F9",
        },
      })
      .then(function (dataUrl: string) {
        var link = document.createElement("a");
        link.download = `mermaid_${Date.now()}.png`;
        link.href = dataUrl;
        link.click();
        setIsDownloadLoading(false);
      })
      .catch(function (error: any) {
        setIsDownloadLoading(false);
      });
  };

  const copyToClipboard = () => {
    copy(chartCodeRef.current.innerText);
    ShowMessage.success("复制成功");
  };
  const copySVGAsPNGToClipboard = async () => {
    setCopyLoading(true);
    const base64Data = `data:image/svg+xml;base64,${Buffer?.from(
      chartRef.current?.innerHTML
    ).toString("base64")}`;
    try {
      const img = new window.Image();
      img.src = base64Data;

      img.onload = async function () {
        // Create a Canvas element
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");
        const scaleFactor = 10;
        // Set canvas size to be scaled by scaleFactor
        canvas.width = img.width * scaleFactor;
        canvas.height = img.height * scaleFactor;

        if (context) {
          // Scale the context to draw at higher resolution
          context.fillStyle = theme == "dark" ? "#282828" : "#F9F9F9";
          context.fillRect(0, 0, canvas.width, canvas.height);
          context.scale(scaleFactor, scaleFactor);

          // Draw the Image onto the Canvas
          context.drawImage(img, 0, 0, img.width, img.height);

          // Convert the Canvas content to a PNG Blob
          canvas.toBlob(async (blob) => {
            if (blob) {
              // Write the PNG Blob to the clipboard
              await navigator.clipboard.write([
                new ClipboardItem({
                  "image/png": blob,
                }),
              ]);
              ShowMessage.success("复制成功");
              setCopyLoading(false);
            }
          }, "image/png");
        }
      };

      img.onerror = function () {
        setCopyLoading(false);
      };
    } catch (err) {
      setCopyLoading(false);
    }
  };
  const imgPreview = useMemo(() => {
    if (!chartRef.current?.innerHTML) {
      return null;
    }
    const base64Image = `data:image/svg+xml;base64,${Buffer?.from(
      chartRef.current?.innerHTML
    ).toString("base64")}`;
    return (
      <div className={styles.imgPreview}>
        <ImageViewer
          image={base64Image}
          visible={isPreviewVisible}
          onClose={() => {
            setIsPreviewVisible(false);
          }}
        />
      </div>
    );
  }, [isPreviewVisible]);
  const mermaidDiv = useMemo(() => {
    return (
      <div
        ref={mermaidRef}
        className={`not-prose rounded-md  border-2  ${
          theme === "dark" ? "border-black" : "border-gray"
        }`}
      >
        <div
          className={`flex h-12 items-center justify-between px-4 ${
            theme == "light" ? " bg-zinc-100 dark:bg-zinc-900" : "bg-zinc-900"
          }`}
        >
          <div className="flex items-center gap-2">
            <Terminal size={16} />
            <p
              className={`text-sm ${
                theme == "light" ? "text-zinc-600 dark:text-zinc-400" : ""
              }`}
            >
              mermaid
            </p>
          </div>
          <Segmented
            options={["Code", "图表"]}
            value={show}
            onChange={(value) => {
              setShow(value); // string
            }}
          />
          <div style={{ width: 80, textAlign: "right" }}>
            {show === "Code" ? (
              <div onClick={copyToClipboard} style={{ cursor: "pointer" }}>
                <CopyOutlined />
              </div>
            ) : (
              <Space>
                {!!navigator?.clipboard && (
                  <div
                    onClick={copySVGAsPNGToClipboard}
                    style={{ cursor: "pointer" }}
                  >
                    {!copyLoading ? <CopyOutlined /> : <LoadingOutlined />}
                  </div>
                )}
                <div onClick={downLoadSvgToPng} style={{ cursor: "pointer" }}>
                  {!downloadLoading ? (
                    <DownloadOutlined />
                  ) : (
                    <LoadingOutlined />
                  )}
                </div>
              </Space>
            )}
          </div>
        </div>
        <div className={`overflow-x-auto`}>
          <div
            className="p-4"
            ref={chartCodeRef}
            style={{
              backgroundColor: theme == "light" ? "#D2D2D2" : "#282828",
              display: show === "Code" ? "block" : "none",
              overflow: "auto",
            }}
          >
            {chartContent}
          </div>
        </div>
        {isChartRight ? (
          <div
            ref={chartRef}
            className="mermaid"
            style={{
              display: show === "Code" ? "none" : "flex",
              justifyContent: "center",
              cursor: "pointer",
              width: "100%",
              maxWidth: "100%",
              overflow: "auto",
            }}
            onClick={() => {
              setIsPreviewVisible(true);
            }}
            key={chartContent}
          ></div>
        ) : (
          <div
            style={{
              display: show === "Code" ? "none" : "flex",
              justifyContent: "center",
            }}
          >
            <Result
              status="warning"
              title="Mermaid语法解析错误，请重新对话或者点击重试"
              description={
                <Button
                  color="primary"
                  loading={requeryLoading}
                  onClick={() => {
                    requeryCount.current = 0;
                    const allowUmlLoadIds = JSON.parse(
                      sessionStorage.getItem("allowUmlLoadIds") || "[]"
                    );
                    const newList = allowUmlLoadIds.filter((item: string) => {
                      return item !== props.uuid;
                    });
                    sessionStorage.setItem(
                      "allowUmlLoadIds",
                      JSON.stringify(newList)
                    );
                    loadMermaid(chartContent, true, 1);
                  }}
                >
                  {requeryLoading ? "重新生成中" : "重新生成"}
                </Button>
              }
            />
          </div>
        )}
        {imgPreview}
      </div>
    );
  }, [
    chartContent,
    imgPreview,
    show,
    theme,
    downloadLoading,
    copyLoading,
    isChartRight,
    requeryLoading,
    isChartVisible,
    props.uuid,
  ]);
  return mermaidDiv;
};

export default MermaidChart;
